import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import {
    Link2, AlertTriangle, Download, CheckCircle, XCircle,
    Search, RefreshCw, ChevronDown, ChevronUp, Filter, Database, Globe
} from "lucide-react";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";
import {
    ContactLinkMatch,
    LinkAnalysisSummary,
    DBClientCompany,
    HubSpotCompanyWithContacts
} from "../types";

type ViewTab = 'matches' | 'unmatched-db' | 'unmatched-hs';
type MatchTypeFilter = 'all' | 'exact_email' | 'normalized_email' | 'gmail_alias' | 'name_match' | 'company_name';
type ConfidenceFilter = 'all' | 'high' | 'medium' | 'low';

const MATCH_TYPE_LABELS: Record<string, string> = {
    exact_email: 'Exact Email',
    normalized_email: 'Normalised Email',
    gmail_alias: 'Gmail Alias',
    name_match: 'Name Match',
    company_name: 'Company Name'
};

const CONFIDENCE_COLORS: Record<string, string> = {
    high: '#2ea043',
    medium: '#d29922',
    low: '#f85149'
};

const CLIENT_STATE_LABELS: Record<string, string> = {
    'l-clientstate-a': 'Active',
    'l-clientstate-p': 'Prospect',
    'l-clientstate-c': 'Ceased',
    'clientstate-onceoffnotpayingforongoingservices': 'Once-off',
    'clientstate-suspended': 'Suspended',
    'clientstate-strikingoff': 'Striking Off',
    'clientstate-onboarding': 'Onboarding',
    'clientstate-test': 'Test',
    'clientstate-internalrelatedtolfrk': 'Internal',
    'clientstate-onhold': 'On Hold',
    'clientstate-onboardingaccounting': 'Onboarding (Acc.)'
};

const formatClientState = (state: string | null): string => {
    if (!state) return 'Unknown';
    return CLIENT_STATE_LABELS[state] || state;
};

const ClientLinkingAnalysis: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();

    const [summary, setSummary] = useState<LinkAnalysisSummary | null>(null);
    const [matches, setMatches] = useState<ContactLinkMatch[]>([]);
    const [unmatchedDb, setUnmatchedDb] = useState<DBClientCompany[]>([]);
    const [unmatchedHs, setUnmatchedHs] = useState<HubSpotCompanyWithContacts[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStage, setLoadingStage] = useState<string>('');

    const [activeTab, setActiveTab] = useState<ViewTab>('matches');
    const [search, setSearch] = useState('');
    const [matchTypeFilter, setMatchTypeFilter] = useState<MatchTypeFilter>('all');
    const [confidenceFilter, setConfidenceFilter] = useState<ConfidenceFilter>('all');
    const [clientStateFilter, setClientStateFilter] = useState<string>('all');

    const getToken = useCallback(async (): Promise<string | null> => {
        return acquireTokenSafe(instance, accounts);
    }, [instance, accounts]);

    const fetchAnalysis = useCallback(async () => {
        const token = await getToken();
        if (!token) {
            setError("Authentication required");
            return;
        }

        setIsLoading(true);
        setLoading(true);
        setError(null);
        setLoadingStage('Fetching DB clients and HubSpot contacts...');

        try {
            const base = getApiBaseUrl();
            const resp = await fetch(`${base}/api/client-linking/analysis`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                throw new Error(errData.details || errData.error || `HTTP ${resp.status}`);
            }

            const data = await resp.json();
            setSummary(data.summary);
            setMatches(data.matches || []);
            setUnmatchedDb(data.unmatchedDbClients || []);
            setUnmatchedHs(data.unmatchedHubSpotCompanies || []);
        } catch (err: any) {
            console.error('Error fetching analysis:', err);
            setError(err.message || 'Failed to fetch analysis');
        } finally {
            setIsLoading(false);
            setLoading(false);
            setLoadingStage('');
        }
    }, [getToken, setLoading]); // eslint-disable-line

    useEffect(() => {
        fetchAnalysis();
    }, [fetchAnalysis]); // eslint-disable-line

    // Filtered matches
    const filteredMatches = useMemo(() => {
        let result = matches;
        if (matchTypeFilter !== 'all') {
            result = result.filter(m => m.matchType === matchTypeFilter);
        }
        if (confidenceFilter !== 'all') {
            result = result.filter(m => m.confidence === confidenceFilter);
        }
        if (clientStateFilter !== 'all') {
            result = result.filter(m => m.dbClientState === clientStateFilter);
        }
        if (search) {
            const s = search.toLowerCase();
            result = result.filter(m =>
                m.dbCompanyName.toLowerCase().includes(s) ||
                m.hsCompanyName.toLowerCase().includes(s) ||
                m.dbContactEmail.toLowerCase().includes(s) ||
                m.hsContactEmail.toLowerCase().includes(s) ||
                m.dbContactName.toLowerCase().includes(s) ||
                m.hsContactName.toLowerCase().includes(s)
            );
        }
        return result;
    }, [matches, matchTypeFilter, confidenceFilter, clientStateFilter, search]);

    // Filtered unmatched DB clients
    const filteredUnmatchedDb = useMemo(() => {
        let result = unmatchedDb;
        if (clientStateFilter !== 'all') {
            result = result.filter(c => c.clientState === clientStateFilter);
        }
        if (search) {
            const s = search.toLowerCase();
            result = result.filter(c =>
                c.companyName.toLowerCase().includes(s) ||
                c.contacts.some(ct => ct.email.toLowerCase().includes(s))
            );
        }
        return result;
    }, [unmatchedDb, clientStateFilter, search]);

    // Filtered unmatched HS companies
    const filteredUnmatchedHs = useMemo(() => {
        if (!search) return unmatchedHs;
        const s = search.toLowerCase();
        return unmatchedHs.filter(c =>
            c.companyName.toLowerCase().includes(s) ||
            (c.domain || '').toLowerCase().includes(s) ||
            c.contacts.some(ct => ct.email.toLowerCase().includes(s))
        );
    }, [unmatchedHs, search]);

    // Distinct client states for filter
    const clientStates = useMemo(() => {
        const states = new Set<string>();
        matches.forEach(m => { if (m.dbClientState) states.add(m.dbClientState); });
        unmatchedDb.forEach(c => { if (c.clientState) states.add(c.clientState); });
        return Array.from(states).sort();
    }, [matches, unmatchedDb]);

    // Match columns
    const matchColumns: TableColumn<ContactLinkMatch>[] = [
        {
            name: 'DB Client',
            selector: row => row.dbCompanyName,
            sortable: true,
            grow: 2,
            cell: row => (
                <div>
                    <div style={{ fontWeight: 600 }}>{row.dbCompanyName}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {formatClientState(row.dbClientState)}
                    </div>
                </div>
            )
        },
        {
            name: 'HubSpot Company',
            selector: row => row.hsCompanyName,
            sortable: true,
            grow: 2,
            cell: row => (
                <div>
                    <div style={{ fontWeight: 600 }}>{row.hsCompanyName}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        ID: {row.hsCompanyId}
                    </div>
                </div>
            )
        },
        {
            name: 'Match Type',
            selector: row => row.matchType,
            sortable: true,
            width: '140px',
            cell: row => (
                <span className={`status-badge ${row.matchType === 'exact_email' ? 'renewable' : row.matchType === 'company_name' ? 'moderate' : 'offline'}`}>
                    {MATCH_TYPE_LABELS[row.matchType] || row.matchType}
                </span>
            )
        },
        {
            name: 'Confidence',
            selector: row => row.confidence,
            sortable: true,
            width: '110px',
            cell: row => (
                <span style={{
                    color: CONFIDENCE_COLORS[row.confidence],
                    fontWeight: 600,
                    textTransform: 'capitalize'
                }}>
                    {row.confidence === 'high' ? '● ' : row.confidence === 'medium' ? '◐ ' : '○ '}
                    {row.confidence}
                </span>
            )
        },
        {
            name: 'DB Contact',
            selector: row => row.dbContactEmail,
            sortable: true,
            grow: 2,
            cell: row => (
                <div>
                    <div style={{ fontSize: '0.85rem' }}>{row.dbContactName || '—'}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{row.dbContactEmail}</div>
                </div>
            )
        },
        {
            name: 'HS Contact',
            selector: row => row.hsContactEmail,
            sortable: true,
            grow: 2,
            cell: row => (
                <div>
                    <div style={{ fontSize: '0.85rem' }}>{row.hsContactName || '—'}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{row.hsContactEmail}</div>
                </div>
            )
        },
        {
            name: 'Match Detail',
            selector: row => row.matchDetail,
            sortable: false,
            grow: 2,
            cell: row => (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {row.matchDetail}
                </div>
            )
        }
    ];

    // Unmatched DB columns
    const unmatchedDbColumns: TableColumn<DBClientCompany>[] = [
        {
            name: 'Company Name',
            selector: row => row.companyName,
            sortable: true,
            grow: 3
        },
        {
            name: 'State',
            selector: row => formatClientState(row.clientState),
            sortable: true,
            width: '130px',
            cell: row => (
                <span className={`status-badge ${row.clientState === 'l-clientstate-a' ? 'renewable' : row.clientState === 'l-clientstate-p' ? 'moderate' : 'offline'}`}>
                    {formatClientState(row.clientState)}
                </span>
            )
        },
        {
            name: 'Contacts',
            selector: row => row.contacts.length,
            sortable: true,
            width: '100px'
        },
        {
            name: 'Contact Emails',
            cell: row => (
                <div style={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
                    {row.contacts.slice(0, 3).map((c, i) => (
                        <div key={i}>{c.email} ({c.source})</div>
                    ))}
                    {row.contacts.length > 3 && (
                        <div style={{ color: 'var(--text-secondary)' }}>+{row.contacts.length - 3} more</div>
                    )}
                </div>
            ),
            grow: 4
        }
    ];

    // Unmatched HS columns
    const unmatchedHsColumns: TableColumn<HubSpotCompanyWithContacts>[] = [
        {
            name: 'Company Name',
            selector: row => row.companyName,
            sortable: true,
            grow: 3
        },
        {
            name: 'Domain',
            selector: row => row.domain || '—',
            sortable: true,
            grow: 2
        },
        {
            name: 'HubSpot ID',
            selector: row => row.hubspotCompanyId,
            sortable: true,
            width: '120px'
        },
        {
            name: 'Contacts',
            selector: row => row.contacts.length,
            sortable: true,
            width: '100px'
        },
        {
            name: 'Contact Emails',
            cell: row => (
                <div style={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
                    {row.contacts.slice(0, 3).map((c, i) => (
                        <div key={i}>{c.email}</div>
                    ))}
                    {row.contacts.length > 3 && (
                        <div style={{ color: 'var(--text-secondary)' }}>+{row.contacts.length - 3} more</div>
                    )}
                </div>
            ),
            grow: 4
        }
    ];

    const handleExport = (format: 'csv' | 'xlsx') => {
        const rows = filteredMatches.map(m => ({
            'DB Company': m.dbCompanyName,
            'DB Client State': formatClientState(m.dbClientState),
            'DB Senta ID': m.dbSentaCompanyId || '',
            'HubSpot Company': m.hsCompanyName,
            'HubSpot Company ID': m.hsCompanyId,
            'Match Type': MATCH_TYPE_LABELS[m.matchType] || m.matchType,
            'Confidence': m.confidence,
            'Match Detail': m.matchDetail,
            'DB Contact Email': m.dbContactEmail,
            'DB Contact Name': m.dbContactName,
            'HS Contact Email': m.hsContactEmail,
            'HS Contact Name': m.hsContactName
        }));

        if (format === 'csv') {
            if (rows.length === 0) return;
            const headers = Object.keys(rows[0]);
            const csvContent = [
                headers.join(','),
                ...rows.map(r => headers.map(h => `"${String((r as any)[h]).replace(/"/g, '""')}"`).join(','))
            ].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `client-hubspot-linking-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } else {
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Matches');

            // Also add unmatched sheets
            if (unmatchedDb.length > 0) {
                const dbRows = unmatchedDb.map(c => ({
                    'Company Name': c.companyName,
                    'Client State': formatClientState(c.clientState),
                    'Senta ID': c.sentaCompanyId || '',
                    'Contact Count': c.contacts.length,
                    'Contact Emails': c.contacts.map(ct => ct.email).join('; ')
                }));
                const ws2 = XLSX.utils.json_to_sheet(dbRows);
                XLSX.utils.book_append_sheet(wb, ws2, 'Unmatched DB');
            }

            if (unmatchedHs.length > 0) {
                const hsRows = unmatchedHs.map(c => ({
                    'Company Name': c.companyName,
                    'Domain': c.domain || '',
                    'HubSpot ID': c.hubspotCompanyId,
                    'Contact Count': c.contacts.length,
                    'Contact Emails': c.contacts.map(ct => ct.email).join('; ')
                }));
                const ws3 = XLSX.utils.json_to_sheet(hsRows);
                XLSX.utils.book_append_sheet(wb, ws3, 'Unmatched HubSpot');
            }

            XLSX.writeFile(wb, `client-hubspot-linking-${new Date().toISOString().slice(0, 10)}.xlsx`);
        }
    };

    const tableTheme = {
        table: { style: { backgroundColor: 'transparent', color: 'var(--text-primary)' } },
        headRow: { style: { backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem', fontWeight: 600 } },
        headCells: { style: { color: 'var(--text-primary)', fontWeight: 600 } },
        rows: { style: { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)', '&:hover': { backgroundColor: 'var(--bg-tertiary)' } } },
        pagination: { style: { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderTop: '1px solid var(--border-color)' } },
        noData: { style: { backgroundColor: 'transparent', color: 'var(--text-secondary)' } }
    };

    if (error && !summary) {
        return (
            <div className="page-container">
                <div className="page-header"><h1><Link2 size={28} /> Client-HubSpot Linking Analysis</h1></div>
                <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
                    <AlertTriangle size={48} color="#f85149" />
                    <h3 style={{ marginTop: 16 }}>Error Loading Analysis</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
                    <button className="btn-primary" onClick={fetchAnalysis} style={{ marginTop: 16 }}>
                        <RefreshCw size={16} /> Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <h1><Link2 size={28} /> Client-HubSpot Linking Analysis</h1>
                <p className="page-subtitle">
                    Compare client contacts from the database with HubSpot contacts to identify shared links
                </p>
            </div>

            {isLoading && (
                <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
                    <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
                    <p style={{ color: 'var(--text-secondary)' }}>{loadingStage || 'Loading...'}</p>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 8 }}>
                        This may take a moment while fetching HubSpot contacts...
                    </p>
                </div>
            )}

            {summary && (
                <>
                    {/* Summary Cards */}
                    <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                        <div className="stat-card">
                            <div className="stat-label"><Database size={16} /> DB Clients</div>
                            <div className="stat-value">{summary.totalDbClients.toLocaleString()}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label"><Globe size={16} /> HubSpot Companies</div>
                            <div className="stat-value">{summary.totalHubSpotCompanies.toLocaleString()}</div>
                        </div>
                        <div className="stat-card" style={{ borderLeft: '3px solid #2ea043' }}>
                            <div className="stat-label"><CheckCircle size={16} /> Matched</div>
                            <div className="stat-value">{summary.matchedDbClients.toLocaleString()}</div>
                            <div className="stat-subtext">
                                {summary.totalDbClients > 0
                                    ? `${((summary.matchedDbClients / summary.totalDbClients) * 100).toFixed(1)}% of DB clients`
                                    : '—'}
                            </div>
                        </div>
                        <div className="stat-card" style={{ borderLeft: '3px solid #f85149' }}>
                            <div className="stat-label"><XCircle size={16} /> Unmatched DB</div>
                            <div className="stat-value">{summary.unmatchedDbClients.toLocaleString()}</div>
                        </div>
                        <div className="stat-card" style={{ borderLeft: '3px solid #d29922' }}>
                            <div className="stat-label"><XCircle size={16} /> Unmatched HubSpot</div>
                            <div className="stat-value">{summary.unmatchedHubSpotCompanies.toLocaleString()}</div>
                        </div>
                    </div>

                    {/* Match Breakdown */}
                    <div className="card" style={{ padding: '20px', marginTop: '16px' }}>
                        <h3 style={{ marginBottom: 12 }}>Match Breakdown</h3>
                        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                            {Object.entries(summary.matchBreakdown).map(([key, val]) => (
                                <div key={key} style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                                        {val}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                        {MATCH_TYPE_LABELS[key] || key}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="card" style={{ padding: '16px', marginTop: '16px' }}>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                            {/* Tab buttons */}
                            <div style={{ display: 'flex', gap: 4, background: 'var(--bg-tertiary)', borderRadius: 8, padding: 4 }}>
                                <button
                                    className={`btn-${activeTab === 'matches' ? 'primary' : 'secondary'}`}
                                    onClick={() => setActiveTab('matches')}
                                    style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                                >
                                    <CheckCircle size={14} /> Matches ({matches.length})
                                </button>
                                <button
                                    className={`btn-${activeTab === 'unmatched-db' ? 'primary' : 'secondary'}`}
                                    onClick={() => setActiveTab('unmatched-db')}
                                    style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                                >
                                    <Database size={14} /> Unmatched DB ({unmatchedDb.length})
                                </button>
                                <button
                                    className={`btn-${activeTab === 'unmatched-hs' ? 'primary' : 'secondary'}`}
                                    onClick={() => setActiveTab('unmatched-hs')}
                                    style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                                >
                                    <Globe size={14} /> Unmatched HS ({unmatchedHs.length})
                                </button>
                            </div>

                            <div style={{ flex: 1 }} />

                            {/* Search */}
                            <div style={{ position: 'relative' }}>
                                <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                                <input
                                    type="text"
                                    placeholder="Search companies, emails..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    style={{
                                        paddingLeft: 34, padding: '8px 12px 8px 34px',
                                        background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
                                        borderRadius: 6, color: 'var(--text-primary)', width: 250
                                    }}
                                />
                            </div>

                            {/* Filters for matches tab */}
                            {activeTab === 'matches' && (
                                <>
                                    <select
                                        value={matchTypeFilter}
                                        onChange={(e) => setMatchTypeFilter(e.target.value as MatchTypeFilter)}
                                        style={{
                                            background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
                                            borderRadius: 6, color: 'var(--text-primary)', padding: '8px 12px'
                                        }}
                                    >
                                        <option value="all">All Match Types</option>
                                        <option value="exact_email">Exact Email</option>
                                        <option value="gmail_alias">Gmail Alias</option>
                                        <option value="name_match">Name Match</option>
                                        <option value="company_name">Company Name</option>
                                    </select>
                                    <select
                                        value={confidenceFilter}
                                        onChange={(e) => setConfidenceFilter(e.target.value as ConfidenceFilter)}
                                        style={{
                                            background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
                                            borderRadius: 6, color: 'var(--text-primary)', padding: '8px 12px'
                                        }}
                                    >
                                        <option value="all">All Confidence</option>
                                        <option value="high">High</option>
                                        <option value="medium">Medium</option>
                                        <option value="low">Low</option>
                                    </select>
                                </>
                            )}

                            {(activeTab === 'matches' || activeTab === 'unmatched-db') && (
                                <select
                                    value={clientStateFilter}
                                    onChange={(e) => setClientStateFilter(e.target.value)}
                                    style={{
                                        background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
                                        borderRadius: 6, color: 'var(--text-primary)', padding: '8px 12px'
                                    }}
                                >
                                    <option value="all">All States</option>
                                    {clientStates.map(s => (
                                        <option key={s} value={s}>{formatClientState(s)}</option>
                                    ))}
                                </select>
                            )}

                            {/* Export */}
                            <button className="btn-secondary" onClick={() => handleExport('csv')} style={{ padding: '8px 12px' }}>
                                <Download size={14} /> CSV
                            </button>
                            <button className="btn-secondary" onClick={() => handleExport('xlsx')} style={{ padding: '8px 12px' }}>
                                <Download size={14} /> XLSX
                            </button>

                            {/* Refresh */}
                            <button className="btn-secondary" onClick={fetchAnalysis} disabled={isLoading} style={{ padding: '8px 12px' }}>
                                <RefreshCw size={14} className={isLoading ? 'spinning' : ''} /> Refresh
                            </button>
                        </div>
                    </div>

                    {/* Data Table */}
                    <div className="card" style={{ marginTop: '16px', overflow: 'hidden' }}>
                        {activeTab === 'matches' && (
                            <DataTable
                                columns={matchColumns}
                                data={filteredMatches}
                                customStyles={tableTheme}
                                pagination
                                paginationPerPage={25}
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                highlightOnHover
                                defaultSortFieldId={4}
                                defaultSortAsc={false}
                                noDataComponent={
                                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
                                        No matches found with current filters
                                    </div>
                                }
                            />
                        )}

                        {activeTab === 'unmatched-db' && (
                            <DataTable
                                columns={unmatchedDbColumns}
                                data={filteredUnmatchedDb}
                                customStyles={tableTheme}
                                pagination
                                paginationPerPage={25}
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                highlightOnHover
                                noDataComponent={
                                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
                                        No unmatched DB clients found
                                    </div>
                                }
                            />
                        )}

                        {activeTab === 'unmatched-hs' && (
                            <DataTable
                                columns={unmatchedHsColumns}
                                data={filteredUnmatchedHs}
                                customStyles={tableTheme}
                                pagination
                                paginationPerPage={25}
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                highlightOnHover
                                noDataComponent={
                                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
                                        No unmatched HubSpot companies found
                                    </div>
                                }
                            />
                        )}
                    </div>

                    {error && (
                        <div className="card" style={{ padding: 16, marginTop: 16, borderLeft: '3px solid #f85149' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#f85149' }}>
                                <AlertTriangle size={16} />
                                <span>{error}</span>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ClientLinkingAnalysis;
